/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ComponentProps } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import {
	IconTriangleWarningOutline18 as AlertTriangle,
	IconAtom2Outline18 as Bot,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
	IconNotepadOutline18 as FileText,
	IconCodeBranchOutline18 as GitBranch,
	IconPhotoAlbumOutline18 as Images,
	IconMediaPlayOutline18 as Play,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { createPortal } from "react-dom"

import type { IssueWithCustomerNeeds } from "@productlane/api/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Shortcut } from "@productlane/ui"
import { CursorIcon } from "@productlane/ui/src/icons/project/cursor-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { useHighlights } from "@/components/editor/editor-config"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useDetailLinkFactory } from "@/lib/detail-drawer"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { IssuePrioritySelect } from "@/screens/app/issues/issue-priority-select"
import { IssueStatusSelect } from "@/screens/app/issues/issue-status-select"
import { useThreadContext } from "../../thread-context"
import { IssueAndProjectSelect } from "./issue-select"
import { useLinkedMenu } from "./linked-menu-context"
import { LinkedItemActions } from "./shared/linked-item-actions"
import { useLinkThreadTarget } from "./use-link-thread-target"

type Props = ComponentProps<"div">

export function LinkedIssues({ className, ...props }: Props) {
	const { thread } = useThreadContext()
	const zero = useZero()
	const { isMenuOpen, onMenuOpenChange } = useLinkedMenu()
	const issues = useMemo(() => thread.issues, [thread])

	const insightIssues = useMemo(() => {
		if (thread && issues && issues.length > 0) {
			return issues.filter((t) => thread.issues.map((i) => i.id).includes(t.id))
		}
		return []
	}, [thread, issues])

	const insightProjects = useMemo(() => {
		const projects = thread.projects
		if (thread && projects && projects.length > 0) {
			return projects.filter((t) =>
				thread.projects.map((p) => p.id).includes(t.id),
			)
		}
		return []
	}, [thread])

	const removeMarks = useCallback((id: string) => {
		const marks = document.querySelectorAll(`mark[data-id="${id}"]`)
		marks.forEach((mark) => {
			mark.replaceWith(mark.innerHTML)
		})
	}, [])

	const { linkIssue, linkProject } = useLinkThreadTarget()

	return (
		<div className={cn("", className)} {...props}>
			<div className="flex items-center">
				<RoleCheck not={["VIEWER"]}>
					<IssueAndProjectSelect
						shortcut="c"
						issueIds={thread.issues.map((i) => i.linearTicketId)}
						side="left"
						modal={false}
						isOpen={isMenuOpen("issue")}
						onOpenChange={onMenuOpenChange("issue")}
						shortcutTooltip={
							<div className="flex items-center gap-1">
								<span>Link or create issue</span>
								<Shortcut>{ShortcutSymbols.C}</Shortcut>
							</div>
						}
						showProjects
						projectIds={thread.projects.map((p) => p.id)}
						onProjectSelect={async ({ internalUpdate, ...project }) => {
							if (thread.projects.map((p) => p.id).includes(project.id)) {
								const existingProject = insightProjects.find(
									(p) => p.id === project.id,
								)

								if (!existingProject) {
									throw new Error("Existing project to unlink not found")
								}
								removeMarks(existingProject.id)
								await mutateOk(
									zero.mutate(
										mutators.projects.unlink({
											feedbackId: thread.id,
											internalProjectId: existingProject.id,
										}),
									),
								)
								return
							}

							await linkProject(project, internalUpdate)
						}}
						onChange={async ({ internalUpdate, ...issue }) => {
							if (
								thread.issues
									.map((i) => i.linearTicketId)
									.includes(issue.linearTicketId)
							) {
								const existingIssue = insightIssues.find(
									(i) => i.linearTicketId === issue.linearTicketId,
								)
								if (!existingIssue) {
									throw new Error("Existing issue to unlink not found")
								}
								removeMarks(existingIssue.linearTicketId)
								// Note: This is a server-side signal mutation
								await mutateOk(
									zero.mutate(
										mutators.issues.unlink({
											feedbackId: thread.id,
											internalIssueId: existingIssue.id,
										}),
									),
								)

								return
							}

							await linkIssue(issue, internalUpdate)
						}}
					/>
				</RoleCheck>
			</div>
		</div>
	)
}

export const ThreadIssuesList = () => {
	const { thread } = useThreadContext()
	const zero = useZero()
	const issues = useMemo(() => thread.issues, [thread])
	const isCustomersEnabled = api.linear.customersEnabled.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const utils = api.useUtils()
	const { role } = useRole()
	const detailLink = useDetailLinkFactory()

	const insightIssues: (IssueWithCustomerNeeds & { isImportant: boolean })[] =
		useMemo(() => {
			if (!(thread && issues && issues.length > 0)) {
				return []
			}

			return issues
				.filter((t) => thread.issues.map((i) => i.id).includes(t.id))
				.map((issue) => ({
					...issue,
					isImportant: issue.customerNeeds.some(
						(cn) => cn.priority === 1 && cn.feedbackId === thread.id,
					),
				}))
		}, [thread, issues])

	const { hoveredIds, setHoveredIds } = useHighlights()
	const removeMarks = useCallback((id: string) => {
		const marks = document.querySelectorAll(`mark[data-id="${id}"]`)
		marks.forEach((mark) => {
			mark.replaceWith(mark.innerHTML)
		})
	}, [])
	const handleRemoveLink = useCallback(
		async (issueId: string) => {
			const existingIssueIds = thread.issues.map((i) => i.id)
			if (existingIssueIds.includes(issueId)) {
				await mutateOk(
					zero.mutate(
						mutators.issues.unlink({
							feedbackId: thread.id,
							internalIssueId: issueId,
						}),
					),
				)
				await utils.thread.getThreadCustomerNeeds.invalidate()
				removeMarks(issueId)
			}
		},
		[thread, zero, utils, removeMarks],
	)

	const handleToggleImportant = useCallback(
		async (issueId: string, isImportant: boolean) => {
			if (role === "VIEWER") {
				return
			}

			if (!isCustomersEnabled.data && !isCustomersEnabled.isLoading) {
				toast.info({
					title: "Linear customers are disabled",
					description:
						"It seems that Customers in Linear are disabled or your plan is outdated. Please enable them in Linear's settings or upgrade your plan to avoid issues with creating customers.",
					action: {
						label: "Open Linear settings",
						callback: () =>
							window.open(
								"https://linear.app/settings/customers-requests",
								"_blank",
							),
					},
				})
			}

			await mutateOk(
				zero.mutate(
					mutators.issues.linkUpdate({
						issueId,
						feedbackId: thread.id,
						priority: Number(isImportant),
					}),
				),
			)
		},
		[role, isCustomersEnabled, zero, thread.id],
	)

	return (
		<>
			{insightIssues.map((issue) => (
				<div
					key={issue.id}
					onMouseEnter={() => {
						setHoveredIds([issue.id])
					}}
					onMouseLeave={() => {
						setHoveredIds([])
					}}
					className={cn(
						"group relative mt-1.5 select-none overflow-hidden rounded-lg border border-gray-200 bg-white/50 p-2 text-sm hover:bg-white dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-800",
						(hoveredIds.includes(issue.linearTicketId) ||
							hoveredIds.includes(issue.id)) &&
							"border-gray-400/90 dark:border-gray-600",
					)}
				>
					<div className="flex items-center gap-1">
						<div
							className="flex items-center"
							onClick={(e) => e.preventDefault()}
						>
							<IssueStatusSelect issue={issue} />
							<IssuePrioritySelect issue={issue} />
						</div>
						<Link
							{...detailLink("issue", issue.id)}
							className="flex items-center gap-1.5"
						>
							<span className="tabular-nums text-secondary">
								{issue.identifier}
							</span>
						</Link>
					</div>
					<Link {...detailLink("issue", issue.id)}>
						<div className="mt-1 truncate pl-1 font-medium text-primary">
							{issue.title}
						</div>
					</Link>

					<LinkedItemActions
						itemId={issue.id}
						itemType="issue"
						linearUrl={issue.url}
						label={`${issue.identifier}: ${issue.title}`}
						isRoadmapVisible={issue.isVisible === true}
						isImportant={issue.isImportant}
						onRemoveLink={handleRemoveLink}
						onToggleImportant={handleToggleImportant}
					/>
					<IssueAgentStatus issueId={issue.id} />
				</div>
			))}
		</>
	)
}

function getAgentStatusConfig(status: string) {
	switch (status) {
		case "PENDING":
		case "CREATING":
			return { label: "Starting", variant: "default" as const }
		case "RUNNING":
			return { label: "Running", variant: "blue" as const }
		case "FINISHED":
			return { label: "Done", variant: "muted" as const }
		case "FAILED":
			return { label: "Failed", variant: "red" as const }
		case "STOPPED":
			return { label: "Stopped", variant: "dark-gray" as const }
		default:
			return { label: status, variant: "default" as const }
	}
}

function formatElapsed(date: Date) {
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
	if (seconds < 60) return `${seconds}s`
	const minutes = Math.floor(seconds / 60)
	if (minutes < 60) return `${minutes}m`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return `${hours}h`
	const days = Math.floor(hours / 24)
	return `${days}d`
}

function useElapsedTime(date: Date | null) {
	const [elapsed, setElapsed] = useState(() =>
		date ? formatElapsed(date) : "",
	)

	useEffect(() => {
		if (!date) return
		setElapsed(formatElapsed(date))
		const interval = setInterval(() => {
			setElapsed(formatElapsed(date))
		}, 1000)
		return () => clearInterval(interval)
	}, [date])

	return elapsed
}

const STUCK_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

function useMetaKey() {
	const [held, setHeld] = useState(false)
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Meta") setHeld(true)
		}
		const onKeyUp = (e: KeyboardEvent) => {
			if (e.key === "Meta" || !e.metaKey) setHeld(false)
		}
		const onBlur = () => setHeld(false)
		window.addEventListener("keydown", onKeyDown)
		window.addEventListener("keyup", onKeyUp)
		window.addEventListener("blur", onBlur)
		return () => {
			window.removeEventListener("keydown", onKeyDown)
			window.removeEventListener("keyup", onKeyUp)
			window.removeEventListener("blur", onBlur)
		}
	}, [])
	return held
}

function IssueAgentStatus({ issueId }: { issueId: string }) {
	const [runs] = useQuery(queries.codingAgentRunsByIssue(issueId))
	const stopMutation = api.codingAgent.stop.useMutation()

	const [expanded, setExpanded] = useState(false)
	const [mediaOpen, setMediaOpen] = useState(false)
	const [mediaIndex, setMediaIndex] = useState(0)
	const [summaryExpanded, setSummaryExpanded] = useState(false)

	// Most recent run shown prominently, older runs in expandable list
	const latestRun = runs[0] ?? null
	const olderRuns = runs.slice(1)

	// Extract viewable media artifacts (videos + images) from latest run
	const mediaItems = useMemo(
		() => getMediaItems(latestRun?.artifacts),
		[latestRun?.artifacts],
	)

	const elapsed = useElapsedTime(
		latestRun?.statusChangedAt ? new Date(latestRun.statusChangedAt) : null,
	)

	const [stuck, setStuck] = useState(false)
	useEffect(() => {
		if (!latestRun?.statusChangedAt) return
		const check = () => {
			const isActive =
				latestRun.status === "PENDING" || latestRun.status === "CREATING"
			const elapsed = Date.now() - new Date(latestRun.statusChangedAt).getTime()
			setStuck(isActive && elapsed > STUCK_TIMEOUT_MS)
		}
		check()
		const interval = setInterval(check, 1000)
		return () => clearInterval(interval)
	}, [latestRun])

	const metaHeld = useMetaKey()
	const [agentHovered, setAgentHovered] = useState(false)

	if (!latestRun) return null

	const statusConfig = stuck
		? { label: "Stuck", variant: "red" as const }
		: getAgentStatusConfig(latestRun.status)

	const canStop =
		latestRun.status === "PENDING" ||
		latestRun.status === "CREATING" ||
		latestRun.status === "RUNNING"

	const cursorSecretAvailable =
		latestRun.provider === "cursor" && !!latestRun.providerRunId
	const cursorSecretActive = cursorSecretAvailable && metaHeld && agentHovered

	return (
		<>
			<div className="mx-1 mt-2 border-t border-gray-100 dark:border-gray-800" />
			<div className="group flex items-center gap-1.5 px-1 pb-0.5 pt-1.5">
				<span
					className={cn(
						"flex items-center gap-1.5",
						cursorSecretActive && "cursor-pointer",
					)}
					onMouseEnter={() => setAgentHovered(true)}
					onMouseLeave={() => setAgentHovered(false)}
					onClick={(e) => {
						if (!cursorSecretAvailable || !e.metaKey) return
						e.stopPropagation()
						window.open(
							`https://cursor.com/agents/${latestRun.providerRunId}`,
							"_blank",
							"noopener,noreferrer",
						)
					}}
				>
					{latestRun.provider === "cursor" ? (
						<CursorIcon
							className={cn(
								"h-3.5 w-3.5 transition-colors",
								cursorSecretActive ? "text-accent" : "text-tertiary",
							)}
						/>
					) : (
						<Bot className="h-3.5 w-3.5 text-tertiary" />
					)}
					<span
						className={cn(
							"text-xs transition-colors",
							cursorSecretActive ? "text-accent" : "text-tertiary",
						)}
					>
						Agent
					</span>
				</span>
				<span className="text-xs text-tertiary">&middot;</span>
				<span
					className={cn(
						"text-xs font-medium",
						AGENT_STATUS_TEXT_COLOR[statusConfig.variant],
					)}
				>
					{statusConfig.label}
				</span>
				{latestRun.status !== "STOPPED" && latestRun.status !== "FINISHED" && (
					<>
						<span className="text-xs text-tertiary">&middot;</span>
						<span className="text-xs tabular-nums text-tertiary">
							{elapsed}
						</span>
					</>
				)}
				<div className="ml-auto flex items-center gap-2">
					{mediaItems.length > 0 && (
						<button
							type="button"
							className="text-tertiary transition-colors hover:text-secondary"
							onClick={(e) => {
								e.stopPropagation()
								setMediaIndex(0)
								setMediaOpen(true)
							}}
						>
							{mediaItems.length === 1 && mediaItems[0]?.type === "video" ? (
								<Play className="h-3.5 w-3.5" />
							) : (
								<Images className="h-3.5 w-3.5" />
							)}
						</button>
					)}
					{!canStop && latestRun.branchName && (
						<a
							href={`https://github.com/${latestRun.repository}/tree/${latestRun.branchName}`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-tertiary transition-colors hover:text-secondary"
							onClick={(e) => e.stopPropagation()}
						>
							<GitBranch className="h-3.5 w-3.5" />
						</a>
					)}
					{latestRun.prUrl && (
						<a
							href={latestRun.prUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-accent text-xs hover:underline"
							onClick={(e) => e.stopPropagation()}
						>
							View PR
						</a>
					)}
					{canStop && !latestRun.prUrl && (
						<button
							type="button"
							className="text-xs text-tertiary opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
							onClick={(e) => {
								e.stopPropagation()
								stopMutation.mutate({ runId: latestRun.id })
							}}
							disabled={stopMutation.isLoading}
						>
							Stop
						</button>
					)}
				</div>
			</div>
			{stuck && (
				<div className="mx-1 mt-1.5 flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1.5 dark:bg-red-500/10">
					<AlertTriangle className="h-3 w-3 flex-shrink-0 text-red-500" />
					<span className="text-xs text-red-600 dark:text-red-400">
						Agent appears stuck. Stop and try again.
					</span>
				</div>
			)}
			{latestRun.status === "FAILED" && latestRun.errorMessage && (
				<div className="mx-1 mt-1.5 flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 dark:bg-red-500/10">
					<AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0 text-red-500" />
					<span className="whitespace-pre-line text-xs text-red-600 dark:text-red-400">
						{latestRun.errorMessage}
					</span>
				</div>
			)}
			{latestRun.status === "FINISHED" && latestRun.summary && (
				<div className="mx-1 mt-1.5">
					<button
						type="button"
						className="flex w-full items-center gap-1 text-xs text-tertiary transition-colors hover:text-secondary"
						onClick={(e) => {
							e.stopPropagation()
							setSummaryExpanded((v) => !v)
						}}
					>
						<span>Summary</span>
						<ChevronDown
							className={cn(
								"h-3 w-3 transition-transform duration-200",
								summaryExpanded && "rotate-180",
							)}
						/>
					</button>
					<p
						className={cn(
							"mt-1 whitespace-pre-line text-xs text-secondary",
							!summaryExpanded && "line-clamp-3",
						)}
					>
						{latestRun.summary}
					</p>
				</div>
			)}
			{olderRuns.length > 0 && (
				<>
					<button
						type="button"
						className="flex w-full items-center justify-end gap-1 px-1 pt-0.5 text-xs text-tertiary transition-colors hover:text-secondary"
						onClick={(e) => {
							e.stopPropagation()
							setExpanded((v) => !v)
						}}
					>
						<span>
							{olderRuns.length} more run
							{olderRuns.length > 1 ? "s" : ""}
						</span>
						<ChevronDown
							className={cn(
								"h-3 w-3 transition-transform duration-200",
								expanded && "rotate-180",
							)}
						/>
					</button>
					{expanded && (
						<div className="space-y-0.5 px-1 pb-0.5">
							{olderRuns.map((run) => (
								<OlderRunRow key={run.id} run={run} />
							))}
						</div>
					)}
				</>
			)}
			{mediaOpen && mediaItems.length > 0 && (
				<ArtifactGallery
					items={mediaItems}
					index={mediaIndex}
					onIndexChange={setMediaIndex}
					onClose={() => setMediaOpen(false)}
				/>
			)}
		</>
	)
}

const AGENT_STATUS_TEXT_COLOR: Record<
	ReturnType<typeof getAgentStatusConfig>["variant"],
	string
> = {
	default: "text-secondary",
	muted: "text-tertiary",
	blue: "text-blue-600 dark:text-blue-400",
	red: "text-red-700 dark:text-red-400",
	"dark-gray": "text-gray-600 dark:text-gray-400",
}

function OlderRunRow({
	run,
}: {
	run: {
		id: string
		status: string
		statusChangedAt: number
		prUrl: string | null
		branchName: string | null
		repository: string
		artifacts: unknown
		summary: string | null
		provider: string
		providerRunId: string | null
	}
}) {
	const statusConfig = getAgentStatusConfig(run.status)
	const elapsed = useElapsedTime(
		run.statusChangedAt ? new Date(run.statusChangedAt) : null,
	)
	const [mediaOpen, setMediaOpen] = useState(false)
	const [mediaIndex, setMediaIndex] = useState(0)
	const [summaryExpanded, setSummaryExpanded] = useState(false)

	const mediaItems = useMemo(
		() => getMediaItems(run.artifacts),
		[run.artifacts],
	)

	const metaHeld = useMetaKey()
	const [iconHovered, setIconHovered] = useState(false)
	const cursorSecretAvailable = run.provider === "cursor" && !!run.providerRunId
	const cursorSecretActive = cursorSecretAvailable && metaHeld && iconHovered

	return (
		<>
			<div className="flex items-center gap-1.5 py-0.5 opacity-60">
				{run.provider === "cursor" ? (
					<CursorIcon
						className={cn(
							"h-3 w-3 transition-colors",
							cursorSecretActive
								? "text-accent cursor-pointer"
								: "text-tertiary",
						)}
						onMouseEnter={() => setIconHovered(true)}
						onMouseLeave={() => setIconHovered(false)}
						onClick={(e) => {
							if (!cursorSecretAvailable || !e.metaKey) return
							e.stopPropagation()
							window.open(
								`https://cursor.com/agents/${run.providerRunId}`,
								"_blank",
								"noopener,noreferrer",
							)
						}}
					/>
				) : (
					<Bot className="h-3 w-3 text-tertiary" />
				)}
				<span
					className={cn(
						"text-xxs font-medium",
						AGENT_STATUS_TEXT_COLOR[statusConfig.variant],
					)}
				>
					{statusConfig.label}
				</span>
				<span className="text-xxs tabular-nums text-tertiary">{elapsed}</span>
				<div className="ml-auto flex items-center gap-2">
					{run.status === "FINISHED" && run.summary && (
						<button
							type="button"
							className="text-tertiary transition-colors hover:text-secondary"
							onClick={(e) => {
								e.stopPropagation()
								setSummaryExpanded((v) => !v)
							}}
						>
							<FileText className="h-3 w-3" />
						</button>
					)}
					{mediaItems.length > 0 && (
						<button
							type="button"
							className="text-tertiary transition-colors hover:text-secondary"
							onClick={(e) => {
								e.stopPropagation()
								setMediaIndex(0)
								setMediaOpen(true)
							}}
						>
							{mediaItems.length === 1 && mediaItems[0]?.type === "video" ? (
								<Play className="h-3 w-3" />
							) : (
								<Images className="h-3 w-3" />
							)}
						</button>
					)}
					{run.branchName && (
						<a
							href={`https://github.com/${run.repository}/tree/${run.branchName}`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-tertiary transition-colors hover:text-secondary"
							onClick={(e) => e.stopPropagation()}
						>
							<GitBranch className="h-3 w-3" />
						</a>
					)}
					{run.prUrl && (
						<a
							href={run.prUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-accent text-xxs hover:underline"
							onClick={(e) => e.stopPropagation()}
						>
							View PR
						</a>
					)}
				</div>
			</div>
			{summaryExpanded && run.summary && (
				<p className="whitespace-pre-line py-0.5 text-xxs text-secondary opacity-60">
					{run.summary}
				</p>
			)}
			{mediaOpen && mediaItems.length > 0 && (
				<ArtifactGallery
					items={mediaItems}
					index={mediaIndex}
					onIndexChange={setMediaIndex}
					onClose={() => setMediaOpen(false)}
				/>
			)}
		</>
	)
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i

interface MediaItem {
	type: "video" | "image"
	url: string
}

function getMediaItems(artifacts: unknown): MediaItem[] {
	if (!artifacts) return []
	const list = artifacts as Array<{ type: string; name: string; url: string }>
	const items: MediaItem[] = []
	for (const a of list) {
		if (a.type === "video") items.push({ type: "video", url: a.url })
		else if (IMAGE_EXTENSIONS.test(a.name) || IMAGE_EXTENSIONS.test(a.url))
			items.push({ type: "image", url: a.url })
	}
	return items
}

function ArtifactGallery({
	items,
	index,
	onIndexChange,
	onClose,
}: {
	items: MediaItem[]
	index: number
	onIndexChange: (i: number) => void
	onClose: () => void
}) {
	const item = items[index]

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
			if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1)
			if (e.key === "ArrowRight" && index < items.length - 1)
				onIndexChange(index + 1)
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [index, items.length, onClose, onIndexChange])

	if (!item) return null

	return createPortal(
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
			onClick={onClose}
		>
			<button
				type="button"
				className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
				onClick={onClose}
			>
				<X className="h-5 w-5" />
			</button>

			{items.length > 1 && index > 0 && (
				<button
					type="button"
					className="absolute left-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
					onClick={(e) => {
						e.stopPropagation()
						onIndexChange(index - 1)
					}}
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
			)}

			{item.type === "video" ? (
				// eslint-disable-next-line jsx-a11y/media-has-caption
				<video
					key={item.url}
					src={item.url}
					controls
					autoPlay
					className="max-h-[90dvh] max-w-[90vw] rounded-lg shadow-2xl"
					onClick={(e) => e.stopPropagation()}
				/>
			) : (
				// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
				<img
					key={item.url}
					src={item.url}
					alt=""
					className="max-h-[90dvh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
					onClick={(e) => e.stopPropagation()}
				/>
			)}

			{items.length > 1 && index < items.length - 1 && (
				<button
					type="button"
					className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
					onClick={(e) => {
						e.stopPropagation()
						onIndexChange(index + 1)
					}}
				>
					<ChevronRight className="h-5 w-5" />
				</button>
			)}

			{items.length > 1 && (
				<span className="absolute bottom-4 text-sm text-white/70">
					{index + 1} / {items.length}
				</span>
			)}
		</div>,
		document.body,
	)
}
